<template>
  <Navigation withExit @back="emit('back')" @exit="emit('exit')" />
  <Title>
    {{ i18n(translations.title) }}
  </Title>

  <p class="my-16">{{ i18n(translations.intro) }}</p>

  <p class="mb-12">
    {{ i18n(translations.shippingDetails) }}
  </p>

  <ShipBackForm
    v-if="returnAddresses && shippingProviders"
    :mostUrgentTask
    :return-addresses
    :shipping-providers
    @done="emit('done')"
  />

  <div v-else>
    <div v-if="shippersOrAddressesPending" class="space-y-16">
      <RevSkeleton height="48px" shape="rectangle" width="100%" />
      <div class="flex space-x-6">
        <RevSkeleton height="48px" shape="rectangle" width="50%" />
        <RevSkeleton height="48px" shape="rectangle" width="50%" />
      </div>
      <RevSkeleton height="48px" shape="rectangle" width="234px" />
    </div>

    <ErrorInfoBlock
      v-if="merchantInfoError || shippingProvidersError"
      @try-again="refresh"
    />
  </div>
</template>

<script setup lang="ts">
import { useAsyncData } from '#imports'
import { computed } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevSkeleton } from '@ds/components/Skeleton'

import type { OrderlineOpenTask } from '~/api/seller-after-sales/types'

import ErrorInfoBlock from '../../../../../../../../components/ErrorInfoBlock/ErrorInfoBlock.vue'
import Navigation from '../Navigation/Navigation.vue'
import Title from '../Title/Title.vue'

import translations from './ShipBackFlow.translations'
import ShipBackForm from './components/ShipBackForm/ShipBackForm.vue'

const i18n = useI18n()

const $httpFetch = useHttpFetch()

const emit = defineEmits(['back', 'exit', 'done'])

defineProps<{
  mostUrgentTask?: OrderlineOpenTask
}>()

const {
  data: returnAddresses,
  status: merchantInfoStatus,
  error: merchantInfoError,
  refresh: refreshMerchantInfo,
} = useAsyncData(
  () => $httpFetch('/bm/customer_request/merchant/information'),
  {
    transform: (data) => data.returnAddresses,
  },
)
const {
  data: shippingProviders,
  status: shippingProvidersStatus,
  error: shippingProvidersError,
  refresh: refreshShippingProviders,
} = useAsyncData(() => $httpFetch('/bm/shippers'), {
  transform: (data) => data.results,
})

const shippersOrAddressesPending = computed(
  () =>
    merchantInfoStatus.value === 'pending' ||
    shippingProvidersStatus.value === 'pending',
)

function refresh() {
  if (merchantInfoError.value && shippingProvidersError.value) {
    refreshMerchantInfo()
    refreshShippingProviders()
  } else if (merchantInfoError.value) {
    refreshMerchantInfo()
  } else if (shippingProvidersError.value) {
    refreshShippingProviders()
  }
}
</script>
